import type { ProgressInfo } from '@thinkalpha/table-client';

/**
 * The table construction state presented by the UI.
 *
 * `pending` deliberately has no numeric value: the percentage is not meaningful
 * until the table server has processed the initial key set.
 */
export type TableConstructionProgress = 'pending' | number | undefined;

export function getTableConstructionProgress(
    update: Pick<ProgressInfo, 'initialKeysetProcessed' | 'percentage'>,
    enhancedProgressEnabled: boolean,
): TableConstructionProgress {
    return enhancedProgressEnabled && !update.initialKeysetProcessed ? 'pending' : update.percentage;
}

/**
 * The rounded percentage from table-client is the presentation boundary. A
 * rounded 100% intentionally ends the progress treatment even if a small tail
 * of requested cells remains unbound.
 */
export function isTableConstructionActive(progress: TableConstructionProgress): boolean {
    return progress === 'pending' || (typeof progress === 'number' && progress < 100);
}

/**
 * Whether a progress update reports a finished table, by the status icon's rule with the pending
 * state always applied: the initial key set is processed and the rounded percentage reached 100.
 * Unlike the presented progress it doesn't depend on `enable-enhanced-table-progress`, so the
 * `0/0` cells of a table still resolving its keys never read as complete.
 */
export function isTableConstructionComplete(
    update: Pick<ProgressInfo, 'initialKeysetProcessed' | 'percentage'>,
): boolean {
    return !isTableConstructionActive(getTableConstructionProgress(update, true));
}
